import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { RegionCatalogo, ZonaCobertura } from '../_models/cobertura';

/** Cobertura de la empresa (la ven todos sus clientes) y el catálogo para editarla. */
export interface CoberturaProveedor { zonas: ZonaCobertura[]; catalogo: RegionCatalogo[] }

export interface CredencialesProveedor { token: string; tokenPermisos: string }

/** Dentro del SPA las credenciales y la navegación vienen de la sesión y el router; embebido, de las propiedades del elemento. */
export interface ContextoProveedor { credenciales(): CredencialesProveedor | null; navegar(ruta: string): void }
export const CONTEXTO_PROVEEDOR = new InjectionToken<ContextoProveedor>('CONTEXTO_PROVEEDOR');

export interface RegistroProveedor { clienteId: string; cliente: string | null; clasificacion: string; estadoAcreditacion: string | null; bloqueo: string | null; tareasAbiertas: number }
/** Una sección de la acreditación que completa el socio; `observacion`: el cliente la devolvió para corregir. */
export interface ItemProveedor { seccionId: string; nombre: string; estado: 'pendiente' | 'completada'; observacion: string | null; enRevision: boolean }
/**
 * Acreditación abierta con un cliente; `diasRestantes` se calcula en la API con la fecha de Chile.
 * `colaboracion`: sin acreditación, las secciones que colabora por completar; no tiene plazo.
 */
export interface TareaProveedor {
  id: string; clienteId: string; cliente: string | null; claseNombre: string; tipo: TipoTarea; estado: 'en_proceso';
  venceEn: string; diasRestantes: number; vencido: boolean; faltan: number; mensaje: string | null; items: ItemProveedor[];
  /** Tareas sin plazo (colaboración, corrección, renovación): desde cuándo está pendiente. */
  desde?: string | null;
}
/** `correccion`: el cliente rechazó un envío; `renovacion`: venció un documento. Ninguna tiene plazo. */
export type TipoTarea = 'ingreso' | 'reacreditacion' | 'colaboracion' | 'correccion' | 'renovacion';
const TITULO_TAREA: Record<TipoTarea, string> = {
  ingreso: 'Acreditación', reacreditacion: 'Re-acreditación', colaboracion: 'Colaboración', correccion: 'Corrección', renovacion: 'Renovación de documentos',
};
const ACCION_TAREA: Partial<Record<TipoTarea, string>> = { correccion: 'Corregir', renovacion: 'Renovar' };
export const tituloTarea = (t: Pick<TareaProveedor, 'tipo'>) => TITULO_TAREA[t.tipo] ?? 'Tarea';
export const accionTarea = (t: Pick<TareaProveedor, 'tipo'>) => ACCION_TAREA[t.tipo] ?? 'Completar';
export const sinPlazo = (t: Pick<TareaProveedor, 'tipo'>) => t.tipo !== 'ingreso' && t.tipo !== 'reacreditacion';
export interface DatoProveedor {
  campoId: string; etiqueta: string; tipo: string; valor: string | null; requerido: boolean; ayuda?: string | null; placeholder?: string | null;
  propuesto?: string | null;
  /** Opciones del selector; `seEdita` = false: el valor sale de un catálogo y lo actualiza el cliente. */
  opciones?: string[] | null; seEdita?: boolean;
  /** Comentario del cliente si rechazó este dato y aún no lo corriges. */
  rechazo?: string | null;
}
export interface DocumentoProveedor {
  documentoSolicitaId: string; nombre: string; tiposPermitidos: string[]; pesoMaximo: string | null;
  solicitaVencimiento: boolean; fechaVencimiento: string | null; cargado: boolean; requerido: boolean; ayuda?: string | null; placeholder?: string | null;
  propuestoNombre?: string | null; propuestoVencimiento?: string | null;
  rechazo?: string | null;
  /** Cuándo vence (frecuencia del documento o su fecha de vencimiento); vencido = hay que renovarlo. */
  vence?: string | null; vencido?: boolean;
}
/** Borrador abierto de la sección: lo que el socio cambió y aún no llega a la ficha. */
export interface EnvioProveedor { estado: 'borrador' | 'pendiente' | 'rechazado'; observacion: string | null; enviadoEn: string | null; cambios: number }
export interface SeccionProveedor {
  seccionId: string; nombre: string; editable: boolean; datos: DatoProveedor[]; documentos: DocumentoProveedor[];
  ayuda?: string | null; periodicidad?: string | null; porActualizar?: boolean; requiereAprobacion?: boolean; envio?: EnvioProveedor | null;
}
/** Un envío propio y su resultado (sin datos de quién lo revisó). */
export interface EnvioPropio {
  seccionId: string; seccion: string; estado: 'pendiente' | 'rechazado' | 'aplicado'; enviadoEn: string | null; cerradoEn: string | null;
  observacion: string | null; items: ItemEnvio[];
}
/** `resultado` por ítem cuando el cliente ya revisó: aprobado | rechazado, con su comentario. */
export interface ItemEnvio { nombre: string; archivo: string | null; resultado: 'aprobado' | 'rechazado' | null; comentario: string | null }
/** Última acreditación cerrada con el cliente. */
export interface ResultadoProveedor { estado: 'aprobado' | 'rechazado'; fecha: string | null; motivo: string | null; claseNombre: string }
export interface HitoProveedor { fecha: string; tipo: string; titulo: string; detalle: string | null }
export const ENVIO_PROPIO: Record<EnvioPropio['estado'], string> = { pendiente: 'En revisión', rechazado: 'Rechazado', aplicado: 'Aplicado en tu ficha' };

export interface FichaProveedor {
  clienteId: string; cliente: string | null; clasificacion: string; estadoAcreditacion: string | null;
  secciones: SeccionProveedor[]; tareas: TareaProveedor[]; resultado?: ResultadoProveedor | null;
}

/** Control del dato según su tipo; null: no se edita desde el portal (sistema o catálogo del cliente). */
export type Control = 'text' | 'number' | 'date' | 'email' | 'tel' | 'textarea' | 'select' | 'checkbox';
const CONTROLES: Record<string, Control> = {
  text: 'text', texto: 'text', textarea: 'textarea', numero: 'number', numerico: 'number', number: 'number', date: 'date', email: 'email', fono: 'tel',
  select: 'select', selector: 'select', checkbox: 'checkbox', casilla: 'checkbox',
};
export function control(d: DatoProveedor): Control | null {
  const c = CONTROLES[d.tipo?.toLowerCase()];
  return !c || d.seEdita === false || (c === 'select' && !d.opciones?.length) ? null : c;
}
export const siNo = (v: string | null | undefined) => (v === 'true' ? 'Sí' : v === 'false' ? 'No' : v || '—');

/** Lo que le falta al socio en la sección: obligatorio sin valor, rechazado o vencido sin reemplazo. */
export function pendientes(s: SeccionProveedor): { faltan: number; total: number } {
  if (!seccionEditable(s)) return { faltan: 0, total: 0 };
  const falta = [
    ...s.datos.filter(d => control(d)).map(d => !!d.rechazo || (d.requerido && !(d.propuesto ?? d.valor))),
    ...s.documentos.map(d => !d.propuestoNombre && (!!d.rechazo || !!d.vencido || (d.requerido && !d.cargado))),
  ];
  return { faltan: falta.filter(Boolean).length, total: falta.length };
}

/** Primero lo que falta, luego lo que puede editar y al final lo de solo lectura (orden estable). */
export const ordenSecciones = (l: SeccionProveedor[]) => {
  const peso = (s: SeccionProveedor) => (pendientes(s).faltan ? 0 : seccionEditable(s) ? 1 : 2);
  return [...l].sort((a, b) => peso(a) - peso(b));
};

/** Firma de los envíos ya revisados, para avisar cuando llega un resultado nuevo. */
export const firmaResultados = (l: EnvioPropio[]) =>
  l.filter(e => e.estado !== 'pendiente').map(e => `${e.seccionId}:${e.estado}:${e.enviadoEn}`).join('|');

const ESTADOS: Record<string, string> = {
  acreditado: 'Acreditado', en_proceso: 'En acreditación', no_acreditado: 'No acreditado', en_acreditacion: 'En acreditación', bloqueado: 'No acreditado',
};
export const estadoTexto = (e: string | null) => (e && ESTADOS[e]) || 'Sin acreditación';

export function envioTexto(s: SeccionProveedor): string {
  const e = s.envio;
  if (!e) return '';
  if (e.estado === 'pendiente') return 'Pendiente de aprobación';
  if (e.estado === 'rechazado') return 'Rechazado, corrige y vuelve a enviar';
  return `Borrador sin enviar (${e.cambios} ${e.cambios === 1 ? 'cambio' : 'cambios'})`;
}

/** Se edita mientras el socio colabora la sección y no está esperando la aprobación. */
export const seccionEditable = (s: SeccionProveedor) => s.editable && s.envio?.estado !== 'pendiente';
export const conRechazos = (s: SeccionProveedor) => [...s.datos, ...s.documentos].some(i => !!i.rechazo);

/** Sin nada pendiente del socio, espera al cliente. `vencido` solo indica que el plazo ya se extendió solo una vez. */
export const enRevision = (t: Pick<TareaProveedor, 'faltan' | 'items'>) => t.items.length > 0 && t.faltan === 0;

export function plazoTexto(t: Pick<TareaProveedor, 'diasRestantes' | 'faltan' | 'items'>): string {
  if (enRevision(t)) return 'En revisión por el cliente';
  if (t.diasRestantes < 0) return 'Plazo vencido: contacta a tu cliente';
  return t.diasRestantes === 0 ? 'Vence hoy' : t.diasRestantes === 1 ? 'Vence mañana' : `Vence en ${t.diasRestantes} días`;
}

/** Fecha 'yyyy-MM-dd' del vencimiento como dd-MM-yyyy, sin desplazarla por la zona horaria. */
export const fechaCorta = (iso: string) => iso.slice(0, 10).split('-').reverse().join('-');

export function mensajeError(e: unknown): string {
  const s = e instanceof HttpErrorResponse ? e.status : 0;
  if (s === 400 || s === 409) return (e as HttpErrorResponse).error?.message || 'Revisa los datos e inténtalo nuevamente.';
  if (s === 403) return 'Esta sección no se puede modificar desde el portal.';
  if (s === 404) return 'No encontramos este registro.';
  if (s === 413) return 'El archivo es demasiado grande.';
  if (s === 429) return 'Hiciste muchas solicitudes seguidas. Espera unos minutos.';
  return 'No pudimos completar la acción. Inténtalo nuevamente.';
}

export const esTokenVencido = (e: unknown) => e instanceof HttpErrorResponse && e.status === 401;

@Injectable({ providedIn: 'root' })
export class PortalProveedorApi {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.urlApi}/proveedor`;

  private headers(c: CredencialesProveedor) {
    return new HttpHeaders({ Authorization: `Bearer ${c.token}`, 'X-Token-Permisos': c.tokenPermisos, 'X-Manejo-Propio': '1' });
  }

  private cliente(clienteId: string) {
    if (!/^\d{1,10}$/.test(clienteId)) throw new Error('clienteId no válido');
    return `${this.url}/clientes/${clienteId}`;
  }

  misRegistros(c: CredencialesProveedor): Observable<RegistroProveedor[]> {
    return this.http.get<RegistroProveedor[]>(`${this.url}/mis-registros`, { headers: this.headers(c) });
  }

  tareas(c: CredencialesProveedor): Observable<TareaProveedor[]> {
    return this.http.get<TareaProveedor[]>(`${this.url}/tareas`, { headers: this.headers(c) });
  }

  ficha(c: CredencialesProveedor, clienteId: string): Observable<FichaProveedor> {
    return this.http.get<FichaProveedor>(`${this.cliente(clienteId)}/ficha`, { headers: this.headers(c) });
  }

  cargarArchivo(c: CredencialesProveedor, clienteId: string, itemId: string, archivo: File, vencimiento?: string): Observable<FichaProveedor> {
    const form = new FormData();
    form.append('archivo', archivo, archivo.name);
    if (vencimiento) form.append('vencimiento', vencimiento);
    return this.http.post<FichaProveedor>(`${this.cliente(clienteId)}/documentos/${encodeURIComponent(itemId)}`, form, { headers: this.headers(c) });
  }

  envios(c: CredencialesProveedor, clienteId: string): Observable<EnvioPropio[]> {
    return this.http.get<EnvioPropio[]>(`${this.cliente(clienteId)}/envios`, { headers: this.headers(c) });
  }

  cargarDatos(c: CredencialesProveedor, clienteId: string, seccionId: string, datos: { campoId: string; valor: string }[]): Observable<FichaProveedor> {
    return this.http.post<FichaProveedor>(`${this.cliente(clienteId)}/secciones/${encodeURIComponent(seccionId)}/datos`, { datos }, { headers: this.headers(c) });
  }

  historial(c: CredencialesProveedor, clienteId: string): Observable<HitoProveedor[]> {
    return this.http.get<HitoProveedor[]>(`${this.cliente(clienteId)}/historial`, { headers: this.headers(c) });
  }

  cobertura(c: CredencialesProveedor, clienteId: string): Observable<CoberturaProveedor> {
    return this.http.get<CoberturaProveedor>(`${this.cliente(clienteId)}/cobertura`, { headers: this.headers(c) });
  }

  guardarCobertura(c: CredencialesProveedor, clienteId: string, zonas: ZonaCobertura[]): Observable<CoberturaProveedor> {
    return this.http.put<CoberturaProveedor>(`${this.cliente(clienteId)}/cobertura`, { zonas }, { headers: this.headers(c) });
  }

  enviarSeccion(c: CredencialesProveedor, clienteId: string, seccionId: string): Observable<FichaProveedor> {
    return this.http.post<FichaProveedor>(`${this.cliente(clienteId)}/secciones/${encodeURIComponent(seccionId)}/enviar`, {}, { headers: this.headers(c) });
  }
}
